/**
 * Authentication Service
 * Handles all authentication-related API calls
 */

import httpService from '../httpService.js'

class AuthService {
  /**
   * Login user
   * @param {Object} credentials - User credentials
   * @param {string} credentials.email - User email
   * @param {string} credentials.password - User password
   * @returns {Promise<Object>} Login response with token and user data
   */
  async login(credentials) {
    const { email, password } = credentials

    const response = await httpService.post('/auth/login', {
      email: email.trim(),
      password,
      // Ce front gère l'étape 2FA (l'engine refuse proprement les anciens bundles encore ouverts)
      mfa_capable: true
    })

    return response
  }

  /**
   * Register new user
   * @param {Object} userData - User registration data
   * @param {string} userData.email - User email
   * @param {string} userData.password - User password
   * @param {string} userData.firstname - User first name
   * @param {string} userData.lastname - User last name
   * @param {string} userData.phone - User phone number
   * @param {boolean} userData.trial - Trial subscription flag
   * @param {string} userData.promo - Promo code (optional)
   * @param {string} queryParams - URL query parameters (optional)
   * @returns {Promise<Object>} Registration response
   */
  async signup(userData, queryParams = '') {
    const {
      email,
      password,
      firstname,
      lastname,
      phone,
      trial = false,
      promo = null,
      locale
    } = userData

    const response = await httpService.post(`/users${queryParams}`, {
      email: email.trim().toLowerCase(),
      password,
      firstname,
      lastname,
      phone,
      trial,
      promo,
      locale
    })

    return response
  }

  /**
   * Verify confirmation email
   * @param {string} token - Verification token
   * @returns {Promise<Object>} Verification response
   */
  async verify(token) {
    const response = await httpService.get(`/users/verify/${token}`)
    return response
  }

  /**
   * Double authentification : étape 2 de la connexion (challenge renvoyé par /auth/login)
   * @param {Object} body - { challenge, code, method: 'totp'|'recovery'|'email' }
   */
  mfaVerify(body) {
    return httpService.post('/auth/mfa/verify', body)
  }

  // Enrôlement exigé par un admin : { challenge, code }
  mfaEnroll(body) {
    return httpService.post('/auth/mfa/enroll', body)
  }

  // Envoi d'un code de connexion par email : { challenge }
  mfaEmail(body) {
    return httpService.post('/auth/mfa/email', body)
  }

  /**
   * Logout user (client-side cleanup)
   * @returns {Promise<boolean>} Logout success
   */
  async logout() {
    try {
      // Clear local storage
      localStorage.removeItem('token')
      localStorage.removeItem('user')
      localStorage.removeItem('userData')

      // Clear session storage
      sessionStorage.clear()

      return { success: true, message: 'Logged out successfully' }
    } catch (error) {
      return {
        success: false,
        error: true,
        message: 'Logout failed',
        originalError: error
      }
    }
  }

  /**
   * Get stored authentication token
   * @returns {string|null} Auth token
   */
  getToken() {
    return localStorage.getItem('token')
  }

  /**
   * Get stored user data
   * @returns {Object|null} User data
   */
  getUser() {
    const userData = localStorage.getItem('user')
    return userData ? JSON.parse(userData) : null
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Authentication status
   */
  isAuthenticated() {
    return !!this.getToken()
  }

  /**
   * Store authentication data
   * @param {string} token - Auth token
   * @param {Object} user - User data
   */
  storeAuthData(token, user) {
    localStorage.setItem('token', token)
    localStorage.setItem('user', JSON.stringify(user))
    localStorage.setItem('session_start', Date.now().toString())
  }

  /**
   * Store authentication data after successful login/signup
   * @param {Object} params - Authentication params
   * @param {string} params.token - Auth token
   * @param {Object} params.user - User data
   */
  handleAuthSuccess(params) {
    this.storeAuthData(params.token, params.user)
    return { success: true }
  }
}

export default new AuthService()
